import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "@/lib/toast-with-sound";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareModal } from "@/components/ShareModal";
import { MapContainer, ZoomControl, Polyline, Marker, CircleMarker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  List, Loader2, Maximize2, Minimize2, Download, Copy as CopyIcon, Pencil,
  Share2, Eye, ChevronDown, Map as MapIcon, Locate, Mountain, BarChart3, Activity, Camera, ArrowLeft, Heart,
  Lock, Globe, EyeOff,
} from "lucide-react";

import MapTileLayer from "@/components/mapShell/MapTileLayer";
import LayerSelector from "@/components/mapShell/LayerSelector";
import MapVisibilityMenu from "@/components/maps/MapVisibilityMenu";
import { MapTooltipsContext, MapTooltip, useTooltipToggle } from "@/components/maps/MapTooltipToggle";
import { MapArrowsContext, useArrowsToggle } from "@/components/maps/MapArrowsToggle";
import ViaShapingDots from "@/components/maps/ViaShapingDots";
import { HoverHighlight } from "@/components/maps/RouteShapes";
import DirectionArrows from "@/components/trip/DirectionArrows";
import {
  MapInstanceCapturer, FitAllSegments, InvalidateOnResize,
} from "@/pages/RoutesTools/editor/EditorMapHelpers";
import {
  SEGMENT_COLORS, junctionIcon, buildAnnotatedMarkers,
} from "@/pages/RoutesTools/editor/editorHelpers";
import {
  startIcon, finishIcon, circularIcon, segmentIcon, fuelIcon, poiIcon,
  hoverIcon, POI_CATEGORY_COLORS,
} from "@/components/maps/mapIcons";
import { SegmentsPanel } from "@/components/routes-tools/SegmentsPanel";
import { PointPhotos } from "@/components/routes-tools/PointPhotos";
import ElevationProfileWeb from "@/components/ElevationProfileWeb";
import ElevationHistogramWeb from "@/components/ElevationHistogramWeb";
import { buildSegmentFromEtapa } from "@/lib/routes-tools/etapaPayload";
// ROUTE-RENDER-MODEL (2026-07-22) — fonte ÚNICA para início/fim + circular,
// partilhada com TripOverviewMap/RouteMapViewer. A ficha pública deixa de
// derivar os extremos dos waypoints nativos (`points[0]/[último]`) e passa a
// usar os EXTREMOS REAIS da geometria desenhada — igual aos mapas do passeio.
import { startEndFromCoords, isCircularRoute } from "@/lib/routes-tools/routeRenderModel";
import { stopsOf, roadsOf, localizedText } from "@/lib/routes-tools/editorPoints";
import { toGPX, buildGmapsStageExports, buildGpxTracksFromSegments, downloadFile, safeBasename } from "@/lib/routes-tools/exporters";
import { GmapsExportDialog } from "@/components/routes-tools/GmapsExportDialog";
import ExportPreflightDialog from "@/components/routes-tools/ExportPreflightDialog";
import { RouteSeo } from "@/components/seo/RouteSeo";
import { SEO } from "@/components/SEO";
import { publicRoutePath } from "@/lib/routes-tools/routeSlug";

const PANEL_MIN_W = 288;
const PANEL_MAX_W = 560;

function haversineKm(a, b) {
  const R = 6371;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLon = (b[1] - a[1]) * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export default function PublicRoutePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // ROUTES-GALLERY — só mostra "Voltar" quando a ficha foi aberta a partir
  // da listagem (state.from). Links partilhados/diretos não o mostram.
  const cameFromGallery = location.state?.from === "/rotas";
  const { t, i18n } = useTranslation("tools");
  const { user } = useAuth();
  const mapInstanceRef = useRef(null);

  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copying, setCopying] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [gmapsOpen, setGmapsOpen] = useState(false);
  const [gpxDialogOpen, setGpxDialogOpen] = useState(false);

  // View state (mirrors the maximized Editor — read-only)
  // On mobile we start map-first: the stages panel and the elevation
  // profile are collapsed so the visitor sees the immersive map; both
  // remain togglable from the toolbar.
  const [panelOpen, setPanelOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 768 : true));
  const [panelWidth, setPanelWidth] = useState(PANEL_MIN_W);
  const [isolatedSegId, setIsolatedSegId] = useState(null);
  const [expandedSegIds, setExpandedSegIds] = useState(() => new Set());
  const [mapView, setMapView] = useState("2d");
  const [showStartEnd, setShowStartEnd] = useState(true);
  const [showJoins, setShowJoins] = useState(true);
  const [showWaypoints, setShowWaypoints] = useState(true);
  const [hiddenPoiCats, setHiddenPoiCats] = useState(() => new Set());
  const { visible: tooltipsVisible, toggle: toggleTooltips } = useTooltipToggle(false);
  const { visible: arrowsVisible, toggle: toggleArrows } = useArrowsToggle();
  const [activeAltPanel, setActiveAltPanel] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? null : "profile"));
  const [hoveredCoord, setHoveredCoord] = useState(null);
  const [hoveredWaypoint, setHoveredWaypoint] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/routes/public/${slug}`)
      .then((res) => setRoute(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // The public route page IS the maximized editor (edge-to-edge, no site
  // chrome). The toolbar toggle requests the browser's NATIVE fullscreen
  // for an even more immersive view; mirror the browser state for the icon.
  useEffect(() => {
    const onFsChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  }, []);

  // Convert the public etapas into the Editor's segment shape so we can
  // reuse the exact SegmentsPanel + map rendering, but read-only.
  const segments = useMemo(
    () => (route?.etapas || []).map((et, i) => buildSegmentFromEtapa(et, i)),
    [route]
  );

  // Default state for the public page: ALL stages expanded. Re-sync on every
  // (re)build of `segments` so the ids always match — segment ids are
  // regenerated on each route load, so a one-shot guard would drift and leave
  // the panel collapsed. `segments` only changes when `route` is (re)fetched,
  // never on user collapse, so this never clobbers manual collapses.
  useEffect(() => {
    if (segments.length > 0) setExpandedSegIds(new Set(segments.map((s) => s.id)));
  }, [segments]);

  const totals = useMemo(() => ({
    distanceKm: segments.reduce((a, s) => a + (Number(s.distanceKm) || 0), 0),
    durationMin: segments.reduce((a, s) => a + (Number(s.durationMin) || 0), 0),
  }), [segments]);

  const presentPoiCats = useMemo(() => {
    const set = new Set();
    segments.forEach((s) => stopsOf(s).forEach((p) => {
      if (p.role === "start" || p.role === "end") return;
      set.add(p.category || "other");
    }));
    return Array.from(set);
  }, [segments]);

  // Elevation profile coords: concatenate per-etapa stored elevation
  // (lat/lng/alt) with cumulative distance + segIndex for per-stage
  // colouring. No extra API call — the public payload already carries it.
  const elevCoords = useMemo(() => {
    const out = [];
    let cumKm = 0;
    let prev = null;
    segments.forEach((seg, si) => {
      const coords = seg?.elevation?.has_altitude ? (seg.elevation.coordinates || []) : [];
      coords.forEach((p) => {
        if (prev) cumKm += haversineKm([prev.lat, prev.lng], [p.lat, p.lng]);
        prev = { lat: p.lat, lng: p.lng };
        out.push({ lat: p.lat, lng: p.lng, alt: p.alt, distance_km: cumKm, segIndex: si });
      });
    });
    return out;
  }, [segments]);

  const hasElevation = elevCoords.length >= 2;
  const showProfile = activeAltPanel === "profile" && hasElevation;
  const showHistogram = activeAltPanel === "histogram" && hasElevation;
  const hasMap = segments.length > 0;
  const isOwner = Boolean(user?.user_id && route?.owner_id && user.user_id === route.owner_id);

  // ── Actions ──────────────────────────────────────────────────────
  const requireAuth = useCallback(() => {
    navigate(`/regista-te?next=${encodeURIComponent(publicRoutePath(route))}`);
  }, [navigate, route]);

  const handleToggleLike = useCallback(async () => {
    if (!route?.route_id || likeLoading) return;
    setLikeLoading(true);
    const wasLiked = !!route.liked;
    try {
      const res = wasLiked
        ? await api.delete(`/api/routes/${route.route_id}/like`)
        : await api.post(`/api/routes/${route.route_id}/like`);
      setRoute((r) => (r ? {
        ...r,
        liked: res.data.liked,
        like_count: res.data.like_count,
        metrics: { ...(r.metrics || {}), likes: res.data.like_count },
      } : r));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("public_route.like_error", "Não foi possível atualizar o like."));
    } finally {
      setLikeLoading(false);
    }
  }, [route, likeLoading, t]);

  const flatLatLng = useMemo(
    () => segments.flatMap((s) => (s.routedCoords?.length >= 2 ? s.routedCoords : s.points.map((p) => [p.lat, p.lng]))),
    [segments]
  );

  // ROUTE-RENDER-MODEL (2026-07-22) — início/fim/circular a partir dos
  // EXTREMOS REAIS da geometria desenhada (mesma `pos` das polylines), via a
  // fonte única `routeRenderModel`. Antes vinham de `points[0]/[último]`
  // (waypoints nativos) através do `buildAnnotatedMarkers`, o que divergia dos
  // mapas do passeio (TripOverviewMap/RouteMapViewer) quando um extremo era
  // uma paragem ligeiramente fora da linha ou não havia waypoints. Respeita o
  // isolamento de etapa (mostra o início/fim DA etapa isolada).
  const boundaries = useMemo(() => {
    const active = isolatedSegId ? segments.filter((s) => s.id === isolatedSegId) : segments;
    const drawCoords = (s) => (s.routedCoords?.length >= 2
      ? s.routedCoords
      : (s.points || []).map((p) => [p.lat, p.lng]));
    const coordsList = active.map(drawCoords).filter((c) => c.length >= 1);
    if (!coordsList.length) return { start: null, end: null, isCircular: false };
    const { start } = startEndFromCoords(coordsList[0], {
      startName: active[0]?.startName || null,
    });
    const { end } = startEndFromCoords(coordsList[coordsList.length - 1], {
      endName: active[active.length - 1]?.endName || null,
    });
    return { start, end, isCircular: isCircularRoute(coordsList.flat()) };
  }, [segments, isolatedSegId]);

  const handleExportGpx = useCallback(() => {
    if (!user) return requireAuth();
    // EDITOR-EXPORT-CENTRALISE (2026-06-19) — abre o seletor de perfil
    // (traçado exato vs recalcular em curvas), tal como o Editor.
    setGpxDialogOpen(true);
  }, [user, requireAuth]);

  const doExportGpx = useCallback((gpxMode = "exact") => {
    // Usa o helper partilhado: GPX idêntico ao do Editor (um `<trk>` por
    // etapa, waypoints início/fim nomeados, PDIs, routeOptions) + perfil.
    const tracks = buildGpxTracksFromSegments(segments);
    if (tracks.length === 0) {
      toast.error(t("public_route.export_gpx_empty", "Esta rota não tem pontos suficientes para exportar."));
      return;
    }
    const gpx = toGPX({ name: route.name, tracks, mode: gpxMode });
    downloadFile(`${safeBasename(route.name)}.gpx`, gpx, "application/gpx+xml");
    toast.success(t("public_route.export_gpx_done", "GPX descarregado."));
    api.post(`/api/routes/public/${route.route_id}/metric`, { kind: "export_gpx" }).catch(() => {});
    setRoute((r) => (r ? { ...r, metrics: { ...(r.metrics || {}), export_gpx: ((r.metrics?.export_gpx) || 0) + 1 } } : r));
  }, [segments, route, t]);

  // EDITOR-EXPORT-CENTRALISE — export por etapa (.zip), igual ao Editor.
  // JSZip carregado dinamicamente para não pesar no bundle da ficha pública.
  const doExportPerStageGpx = useCallback(async (gpxMode = "exact") => {
    if (segments.length < 2) return;
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const base = safeBasename(route.name) || "mototrips-route";
      const used = new Map();
      segments.forEach((s, i) => {
        const track = buildGpxTracksFromSegments([s])[0];
        if (!track) return;
        const stageName = s.name || `Etapa ${i + 1}`;
        const stageBase = safeBasename(stageName) || `etapa-${i + 1}`;
        const baseFn = `${i + 1}-${stageBase}`;
        const count = (used.get(baseFn) || 0) + 1;
        used.set(baseFn, count);
        const fname = count === 1 ? `${baseFn}.gpx` : `${baseFn}-${count}.gpx`;
        zip.file(fname, toGPX({ name: stageName, tracks: [{ ...track, name: stageName }], mode: gpxMode }));
      });
      if (Object.keys(zip.files).length === 0) {
        toast.error(t("public_route.export_gpx_empty", "Esta rota não tem pontos suficientes para exportar."));
        return;
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${base}-etapas.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("public_route.export_gpx_done", "GPX descarregado."));
      api.post(`/api/routes/public/${route.route_id}/metric`, { kind: "export_gpx" }).catch(() => {});
      setRoute((r) => (r ? { ...r, metrics: { ...(r.metrics || {}), export_gpx: ((r.metrics?.export_gpx) || 0) + 1 } } : r));
    } catch {
      toast.error(t("public_route.export_gpx_empty", "Esta rota não tem pontos suficientes para exportar."));
    }
  }, [segments, route, t]);

  const handleExportGmaps = useCallback(() => {
    if (!user) return requireAuth();
    // Unified per-stage export (smart-sampled, optional auto-split). The
    // shared dialog derives URLs from `segments`; never auto-opens Maps.
    if (buildGmapsStageExports(segments).length === 0) {
      toast.error(t("public_route.export_gmaps_empty", "Esta rota não tem pontos suficientes para exportar."));
      return;
    }
    setGmapsOpen(true);
    api.post(`/api/routes/public/${route.route_id}/metric`, { kind: "export_gmaps" }).catch(() => {});
    setRoute((r) => (r ? { ...r, metrics: { ...(r.metrics || {}), export_gmaps: ((r.metrics?.export_gmaps) || 0) + 1 } } : r));
  }, [user, requireAuth, segments, route, t]);

  const handleCopy = useCallback(async () => {
    if (!user) return requireAuth();
    setCopying(true);
    try {
      await api.post(`/api/my-routes/${route.route_id}/copy`);
      toast.success(t("public_route.copy_done", "Rota copiada para a tua biblioteca."));
      navigate("/gestaorotas");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) toast.error(t("public_route.copy_limit", "Atingiste o limite de rotas do teu plano."));
      else toast.error(t("public_route.copy_error", "Erro ao copiar a rota."));
    } finally {
      setCopying(false);
    }
  }, [user, requireAuth, route, navigate, t]);

  const flyTo = useCallback((lat, lng, z = 14) => {
    const m = mapInstanceRef.current;
    if (m && Number.isFinite(lat) && Number.isFinite(lng)) m.flyTo([lat, lng], z, { duration: 0.6 });
  }, []);

  // ── Loading / not-found (full-viewport, no site chrome) ──────────
  if (loading) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center" data-testid="public-route-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (notFound || !route) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center text-center gap-3 px-6" data-testid="public-route-not-found">
        {/* Soft-404 hardening: SPA can't return HTTP 404, so emit noindex so
            Google drops the URL instead of flagging Soft 404 (2026-07). */}
        <SEO title={t("public_route.not_found_title", "Rota não disponível")} noindex />
        <MapIcon className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-2xl font-bold">{t("public_route.not_found_title", "Rota não disponível")}</h1>
        <p className="text-muted-foreground max-w-md">{t("public_route.not_found_desc", "Esta rota não existe ou já não é pública.")}</p>
        <Button onClick={() => navigate("/")} variant="outline" data-testid="public-route-home">{t("public_route.go_home", "Ir para a entrada")}</Button>
      </div>
    );
  }

  const author = route.author || {};
  const authorCornerPath = author.corner_url || `/meu-canto?motard=${route.owner_id}`;
  const visItems = [
    { key: "start-end", label: t("public_route.toggle_start_end", "Início e fim"), checked: showStartEnd, onCheckedChange: setShowStartEnd },
    { key: "joins", label: t("public_route.toggle_joins", "Junções"), checked: showJoins, onCheckedChange: setShowJoins },
    { key: "waypoints", label: t("public_route.toggle_pois", "Paragens"), checked: showWaypoints, onCheckedChange: setShowWaypoints },
    { key: "sep-1", separator: true },
    { key: "labels", label: t("public_route.toggle_labels", "Etiquetas"), checked: tooltipsVisible, onCheckedChange: toggleTooltips },
    { key: "arrows", label: t("public_route.toggle_arrows", "Setas de direcção"), checked: arrowsVisible, onCheckedChange: toggleArrows },
    ...(presentPoiCats.length > 0 ? [{ key: "sep-poi", separator: true }] : []),
    ...presentPoiCats.map((cat) => ({
      key: `poi-${cat}`,
      label: (
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: POI_CATEGORY_COLORS[cat] || POI_CATEGORY_COLORS.other }} />
          {t(`editor.poi.categories.${cat}`, cat)}
        </span>
      ),
      checked: !hiddenPoiCats.has(cat),
      onCheckedChange: (v) => setHiddenPoiCats((prev) => {
        const next = new Set(prev);
        if (v) next.delete(cat); else next.add(cat);
        return next;
      }),
    })),
  ];

  // SEO-DRAW / Soft-404 hardening (2026-06-15): production does NOT route
  // bots to the per-route prerender (/api/seo/render) — indexation relies
  // purely on Google's WRS rendering this React page. Map-heavy pages can
  // read as "thin" → Soft 404. So we emit an early, semantic, crawlable
  // text summary (visually hidden, fully crawlable) + a stage list so the
  // rendered DOM clearly describes a unique content page.
  const seoLng = (i18n.language || "pt").slice(0, 2);
  const seoLocText = [route.start_locality_global, route.end_locality_global].filter(Boolean).join(" → ");
  const seoDist = Math.round(Number(route.total_distance_km) || 0);
  const seoOwner = author?.name || "";
  const seoLead = {
    pt: `Rota de mota${seoLocText ? ` por ${seoLocText}` : ""} — ${seoDist} km.${seoOwner ? ` Partilhada por ${seoOwner} no MotoTrips.` : " Partilhada no MotoTrips."}`,
    en: `Motorcycle route${seoLocText ? ` through ${seoLocText}` : ""} — ${seoDist} km.${seoOwner ? ` Shared by ${seoOwner} on MotoTrips.` : " Shared on MotoTrips."}`,
    es: `Ruta en moto${seoLocText ? ` por ${seoLocText}` : ""} — ${seoDist} km.${seoOwner ? ` Compartida por ${seoOwner} en MotoTrips.` : " Compartida en MotoTrips."}`,
    fr: `Itinéraire moto${seoLocText ? ` à travers ${seoLocText}` : ""} — ${seoDist} km.${seoOwner ? ` Partagé par ${seoOwner} sur MotoTrips.` : " Partagé sur MotoTrips."}`,
  }[seoLng] || `Rota de mota — ${seoDist} km.`;
  const seoStageNames = (route.etapas || []).map((e) => e?.name).filter(Boolean);

  const shell = (
    <div className="flex-1 flex flex-col min-h-0" data-testid="public-route-page">
      {/* Crawlable SEO summary (visually hidden, fully indexable). */}
      <section className="sr-only" data-testid="public-route-seo-summary">
        <h2>{route.name}</h2>
        <p>{seoLead} {t("public_route.seo_suffix")}</p>
        {seoStageNames.length > 0 && (
          <>
            <h3>{t("public_route.stages_heading")}</h3>
            <ul>
              {seoStageNames.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </>
        )}
      </section>
      {/* Edge-to-edge maximized map surface (mirrors the Editor maximized) */}
      <div
        className="flex-1 overflow-hidden bg-card/40 relative flex flex-col min-h-0"
        data-testid="public-route-map-container"
      >
        {/* Prominent "back to gallery" bar — only when arriving from /rotas */}
        {cameFromGallery && (
          <div
            className="flex items-center px-2 py-2 sm:px-3 border-b border-border bg-primary/10 relative z-[1002]"
            data-testid="public-route-back-bar"
          >
            <Button
              onClick={() => navigate("/rotas")}
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary hover:bg-primary/15 font-semibold"
              data-testid="public-route-back-to-gallery"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t("public_route.back_to_routes", "Voltar às rotas")}
            </Button>
          </div>
        )}
        {/* PREVIEW-FICHA (2026-07-02) — status banner. Owner previewing a
            private route sees a prominent warning; unlisted/public get a
            clear coloured indicator so the state is never ambiguous. */}
        {route.is_private_preview ? (
          <div
            className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/40 bg-amber-500/15 text-amber-200 relative z-[1002]"
            data-testid="public-route-preview-banner"
          >
            <Lock className="h-4 w-4 shrink-0" />
            <span className="text-xs sm:text-sm font-semibold">
              {t("public_route.preview_private", "PRÉ-VISUALIZAÇÃO — esta rota está PRIVADA. Só tu a vês. Torna-a pública na gestão de rotas para partilhar.")}
            </span>
            {route.fully_inside_home_zone && (
              <span className="text-[11px] sm:text-xs opacity-90" data-testid="public-route-preview-homezone-warn">
                {t("public_route.preview_homezone", "(fica dentro da tua zona de privacidade — não poderá ser publicada assim)")}
              </span>
            )}
          </div>
        ) : route.visibility === "unlisted" ? (
          <div
            className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/40 bg-amber-500/15 text-amber-200 relative z-[1002]"
            data-testid="public-route-unlisted-banner"
          >
            <EyeOff className="h-4 w-4 shrink-0" />
            <span className="text-xs sm:text-sm font-semibold">
              {t("public_route.status_unlisted", "Rota RESTRITA — acessível apenas por link, não aparece na galeria pública.")}
            </span>
          </div>
        ) : route.visibility === "public" && isOwner ? (
          <div
            className="flex items-center gap-2 px-3 py-1.5 border-b border-green-600/40 bg-green-600/15 text-green-300 relative z-[1002]"
            data-testid="public-route-public-banner"
          >
            <Globe className="h-3.5 w-3.5 shrink-0" />
            <span className="text-[11px] sm:text-xs font-semibold">
              {t("public_route.status_public", "Rota PÚBLICA — visível na galeria de rotas.")}
            </span>
          </div>
        ) : null}
        {/* Single compact top toolbar (route+author left, controls + actions right) */}
        <div
          className="flex items-center gap-1 sm:gap-1.5 px-2 py-2 sm:px-3 sm:py-3 border-b border-border bg-card/95 backdrop-blur-sm relative z-[1001] flex-wrap"
          data-testid="public-route-top-toolbar"
        >
          {/* MotoTrips brand mark — links to the home (this page has no site header) */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate("/"); }}
            className="shrink-0 rounded-lg hover:opacity-80 transition-opacity"
            data-testid="public-route-logo-link"
            aria-label={t("public_route.logo_tooltip")}
            title={t("public_route.logo_tooltip")}
          >
            <img
              src="/logo-mototrips.png"
              alt="MotoTrips"
              className="h-9 sm:h-14 lg:h-20 w-auto object-contain mr-0.5 sm:mr-1.5 select-none"
              data-testid="public-route-logo"
            />
          </a>

          {/* Route name + author — styled like the Editor's route-name box */}
          <button
            onClick={() => navigate(authorCornerPath)}
            className="flex items-center gap-2 mr-1 min-w-0 max-md:flex-1 max-md:max-w-none md:shrink-0 md:max-w-[42vw] rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-1 hover:bg-primary/15 transition-colors"
            data-testid="public-route-title-box"
            title={t("public_route.author_corner_tooltip", { name: author.name || "" })}
          >
            <Avatar className="h-7 w-7 border border-primary/50 shrink-0">
              <AvatarImage src={author.avatar} alt={author.name} />
              <AvatarFallback className="bg-primary/20 text-xs">{(author.name || "M").charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 text-left">
              <p className="text-[9px] uppercase tracking-wider text-primary/90 font-semibold leading-none flex items-center gap-1.5">
                <span className="truncate max-w-[140px]" data-testid="public-route-author-name">{author.name}</span>
              </p>
              <h1 className="text-sm font-bold text-foreground truncate leading-tight" data-testid="public-route-title">{route.name}</h1>
            </div>
          </button>

          {/* Like (logged-in only) + Metrics buttons to the RIGHT of the title card */}
          {user && (
            <button
              onClick={handleToggleLike}
              disabled={likeLoading}
              className="flex items-center gap-1 hover:bg-muted rounded-lg px-1.5 py-1.5 sm:px-2 sm:py-2 text-foreground shrink-0 disabled:opacity-60"
              data-testid="public-route-like-button"
              aria-label={t("public_route.like", "Gosto")}
              title={t("public_route.like", "Gosto")}
            >
              <Heart className={`h-4 w-4 ${route.liked ? "fill-red-500 text-red-500" : ""}`} />
              <span className="text-xs font-semibold tabular-nums" data-testid="public-route-like-count">{route.like_count || 0}</span>
            </button>
          )}
          <button
            onClick={() => setMetricsOpen(true)}
            className="hover:bg-muted rounded-lg p-1.5 sm:p-2 text-foreground shrink-0"
            data-testid="public-route-metrics-trigger"
            aria-label={t("public_route.metrics_title", "Métricas da rota")}
            title={t("public_route.metrics_title", "Métricas da rota")}
          >
            <Activity className="h-4 w-4" />
          </button>

          {/* ───────────────── DESKTOP controls (single row) ─────────────────
              Unchanged horizontal toolbar; hidden on mobile (<768px). */}
          <div className="hidden md:flex md:flex-1 md:items-center md:gap-1.5 md:flex-wrap">
            {/* Panel (Etapas) toggle stays on the left, next to the title */}
            <button
              onClick={() => setPanelOpen((v) => !v)}
              className={`hover:bg-muted rounded-lg p-1.5 sm:p-2 ${panelOpen ? "text-orange-500" : "text-foreground"}`}
              data-testid="public-route-toggle-panel"
              aria-label={t("public_route.toggle_stages", "Etapas")}
              title={t("public_route.toggle_stages", "Etapas")}
            >
              <List className="h-4 w-4" />
            </button>

            {/* Actions block (Copiar / Exportar / Partilhar / Editar) — left side */}
            {user && !isOwner && (
              <Button onClick={handleCopy} disabled={copying} variant="ghost" size="sm" className="hover:bg-muted" data-testid="public-route-copy" title={t("public_route.copy", "Fazer cópia para a Gestão de Rotas")}>
                {copying ? <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" /> : <CopyIcon className="h-4 w-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">{t("public_route.copy", "Fazer cópia para a Gestão de Rotas")}</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="hover:bg-muted" data-testid="public-route-export-trigger" title={t("public_route.export", "Exportar")}>
                  <Download className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("public_route.export", "Exportar")}</span> <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[10001]">
                <DropdownMenuItem onClick={handleExportGpx} data-testid="public-route-export-gpx">GPX</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportGmaps} data-testid="public-route-export-gmaps">{t("public_route.export_gmaps", "Google Maps")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ShareModal
              type="route"
              noTooltip
              data={{ path: publicRoutePath(route), name: route.name }}
              customTrigger={
                <Button variant="ghost" size="sm" className="hover:bg-muted" data-testid="public-route-share" title={t("public_route.share", "Partilhar")}>
                  <Share2 className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("public_route.share", "Partilhar")}</span>
                </Button>
              }
            />
            {isOwner && (
              <Button onClick={() => navigate(`/rotas/ferramentas/editor?edit_route=${route.route_id}`)} variant="ghost" size="sm" className="hover:bg-muted" data-testid="public-route-edit" title={t("public_route.edit", "Editar")}>
                <Pencil className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("public_route.edit", "Editar")}</span>
              </Button>
            )}

            {/* Right cluster — map/view controls + language + fullscreen */}
            <div className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">
              <LayerSelector view={mapView} onChange={setMapView} testIdPrefix="public-route-layers" variant="header" hideOnMobile={false} bordered={false} />
              <MapVisibilityMenu testIdPrefix="public-route-markers" disabled={!hasMap} items={visItems} />
              <button
                onClick={() => { setIsolatedSegId(null); setRecenterTrigger((n) => n + 1); }}
                disabled={!hasMap}
                className="hover:bg-muted rounded-lg p-1.5 sm:p-2 text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="public-route-recenter"
                aria-label={t("public_route.center", "Centrar")}
                title={t("public_route.center", "Centrar")}
              >
                <Locate className="h-4 w-4" />
              </button>
              <button
                onClick={() => setActiveAltPanel((p) => (p === "profile" ? null : "profile"))}
                disabled={!hasElevation}
                className={`hover:bg-muted rounded-lg p-1.5 sm:p-2 disabled:opacity-40 disabled:cursor-not-allowed ${showProfile ? "text-orange-500" : "text-foreground"}`}
                data-testid="public-route-toggle-elevation"
                aria-label={t("public_route.elevation_profile", "Perfil de altitude")}
                title={t("public_route.elevation_profile", "Perfil de altitude")}
              >
                <Mountain className="h-4 w-4" />
              </button>
              <button
                onClick={() => setActiveAltPanel((p) => (p === "histogram" ? null : "histogram"))}
                disabled={!hasElevation}
                className={`hover:bg-muted rounded-lg p-1.5 sm:p-2 disabled:opacity-40 disabled:cursor-not-allowed ${showHistogram ? "text-orange-500" : "text-foreground"}`}
                data-testid="public-route-toggle-histogram"
                aria-label={t("public_route.histogram", "Histograma")}
                title={t("public_route.histogram", "Histograma")}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
              <LanguageSwitcher variant="ghost" size="sm" className="text-foreground" noTooltip />
              <button
                onClick={toggleFullscreen}
                className="hover:bg-muted rounded-lg p-1.5 sm:p-2 text-foreground"
                data-testid="public-route-fullscreen-toggle"
                aria-label={fullscreen ? t("public_route.map_close_full", "Fechar ecrã completo") : t("public_route.map_full", "Ecrã completo")}
                title={fullscreen ? t("public_route.map_close_full", "Fechar ecrã completo") : t("public_route.map_full", "Ecrã completo")}
              >
                {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* ───────────────── MOBILE controls (two rows) ─────────────────
              Row A: primary actions as bottom-nav style (icon over label).
              Row B: secondary map controls as plain icons.
              Fullscreen is intentionally omitted on mobile. */}
          <div className="flex md:hidden basis-full flex-col gap-1.5 mt-1">
            {/* Row A — Etapas · Exportar · Partilhar · Copiar/Editar · Idioma */}
            <div className="flex items-start justify-around w-full">
              <button
                onClick={() => setPanelOpen((v) => !v)}
                className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted min-w-[58px] ${panelOpen ? "text-orange-500" : "text-foreground"}`}
                data-testid="public-route-toggle-panel-m"
                aria-label={t("public_route.toggle_stages", "Etapas")}
              >
                <List className="h-5 w-5" />
                <span className="text-[10px] leading-tight text-center">{t("public_route.toggle_stages", "Etapas")}</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted text-foreground min-w-[58px]"
                    data-testid="public-route-export-trigger-m"
                    aria-label={t("public_route.export", "Exportar")}
                  >
                    <Download className="h-5 w-5" />
                    <span className="text-[10px] leading-tight text-center">{t("public_route.export", "Exportar")}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="z-[10001]">
                  <DropdownMenuItem onClick={handleExportGpx} data-testid="public-route-export-gpx-m">GPX</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportGmaps} data-testid="public-route-export-gmaps-m">{t("public_route.export_gmaps", "Google Maps")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ShareModal
                type="route"
                noTooltip
                data={{ path: publicRoutePath(route), name: route.name }}
                customTrigger={
                  <button
                    className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted text-foreground min-w-[58px]"
                    data-testid="public-route-share-m"
                    aria-label={t("public_route.share", "Partilhar")}
                  >
                    <Share2 className="h-5 w-5" />
                    <span className="text-[10px] leading-tight text-center">{t("public_route.share", "Partilhar")}</span>
                  </button>
                }
              />
              {user && !isOwner && (
                <button
                  onClick={handleCopy}
                  disabled={copying}
                  className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted text-foreground min-w-[58px] disabled:opacity-40"
                  data-testid="public-route-copy-m"
                  aria-label={t("public_route.copy_short", "Copiar")}
                >
                  {copying ? <Loader2 className="h-5 w-5 animate-spin" /> : <CopyIcon className="h-5 w-5" />}
                  <span className="text-[10px] leading-tight text-center">{t("public_route.copy_short", "Copiar")}</span>
                </button>
              )}
              {isOwner && (
                <button
                  onClick={() => navigate(`/rotas/ferramentas/editor?edit_route=${route.route_id}`)}
                  className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted text-foreground min-w-[58px]"
                  data-testid="public-route-edit-m"
                  aria-label={t("public_route.edit", "Editar")}
                >
                  <Pencil className="h-5 w-5" />
                  <span className="text-[10px] leading-tight text-center">{t("public_route.edit", "Editar")}</span>
                </button>
              )}
              <LanguageSwitcher variant="ghost" noTooltip stacked stackedLabel={t("public_route.language", "Idioma")} testId="language-switcher-m" />
            </div>
            {/* Row B — Mapa · Marcadores · Centrar · Perfil · Histograma (icons) */}
            <div className="flex items-center justify-center gap-2 w-full border-t border-border/60 pt-1.5">
              <LayerSelector view={mapView} onChange={setMapView} testIdPrefix="public-route-layers-m" variant="header" hideOnMobile={false} bordered={false} iconOnly />
              <MapVisibilityMenu testIdPrefix="public-route-markers-m" disabled={!hasMap} items={visItems} />
              <button
                onClick={() => { setIsolatedSegId(null); setRecenterTrigger((n) => n + 1); }}
                disabled={!hasMap}
                className="hover:bg-muted rounded-lg p-2 text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="public-route-recenter-m"
                aria-label={t("public_route.center", "Centrar")}
                title={t("public_route.center", "Centrar")}
              >
                <Locate className="h-4 w-4" />
              </button>
              <button
                onClick={() => setActiveAltPanel((p) => (p === "profile" ? null : "profile"))}
                disabled={!hasElevation}
                className={`hover:bg-muted rounded-lg p-2 disabled:opacity-40 disabled:cursor-not-allowed ${showProfile ? "text-orange-500" : "text-foreground"}`}
                data-testid="public-route-toggle-elevation-m"
                aria-label={t("public_route.elevation_profile", "Perfil de altitude")}
                title={t("public_route.elevation_profile", "Perfil de altitude")}
              >
                <Mountain className="h-4 w-4" />
              </button>
              <button
                onClick={() => setActiveAltPanel((p) => (p === "histogram" ? null : "histogram"))}
                disabled={!hasElevation}
                className={`hover:bg-muted rounded-lg p-2 disabled:opacity-40 disabled:cursor-not-allowed ${showHistogram ? "text-orange-500" : "text-foreground"}`}
                data-testid="public-route-toggle-histogram-m"
                aria-label={t("public_route.histogram", "Histograma")}
                title={t("public_route.histogram", "Histograma")}
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

      {/* Row: read-only stages panel + map column */}
      <div className="flex-1 flex flex-row min-h-0 relative">
        {panelOpen && (
          <>
            <div className="max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:z-[1200] max-md:shadow-2xl bg-card flex">
              <SegmentsPanel
                readOnly
                forceSingleGroup={route?.origin === "go_recording"}
                segments={segments}
                totals={{ km: totals.distanceKm, durationMin: totals.durationMin }}
                expandedSegIds={expandedSegIds}
                isolatedId={isolatedSegId}
                onToggleIsolate={(id) => setIsolatedSegId((cur) => (cur === id ? null : id))}
                onToggleExpand={(id) => setExpandedSegIds((s) => {
                  const next = new Set(s);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                })}
                onCollapseAll={() => setExpandedSegIds(new Set())}
                onExpandAll={() => setExpandedSegIds(new Set(segments.map((s) => s.id)))}
                onClose={() => setPanelOpen(false)}
                width={panelWidth}
                minWidth={PANEL_MIN_W}
                maxWidth={PANEL_MAX_W}
                onWidthChange={setPanelWidth}
                maxSegments={segments.length}
                onHoverWaypoint={setHoveredWaypoint}
                onClickWaypoint={(wp) => flyTo(wp?.lat, wp?.lng)}
              />
            </div>
            <div className="md:hidden absolute inset-0 z-[1100] bg-black/40" onClick={() => setPanelOpen(false)} data-testid="public-route-panel-backdrop" />
          </>
        )}

        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 relative min-h-0">
            {hasMap ? (
              <MapTooltipsContext.Provider value={tooltipsVisible}>
                <MapArrowsContext.Provider value={arrowsVisible}>
                  <MapContainer
                    center={[flatLatLng[0][0], flatLatLng[0][1]]}
                    zoom={11}
                    style={{ height: "100%", width: "100%" }}
                    zoomControl={false}
                    scrollWheelZoom
                    zoomAnimation={false}
                    fadeAnimation={false}
                  >
                    <MapTileLayer view={mapView} />
                    <ZoomControl position="topright" />
                    <MapInstanceCapturer onReady={(m) => { mapInstanceRef.current = m; }} />
                    <InvalidateOnResize trigger={`${fullscreen}|${panelOpen}|${panelWidth}|${activeAltPanel}|${recenterTrigger}`} />

                    {/* Per-segment polylines (respect isolation) */}
                    {segments.map((s) => {
                      if (isolatedSegId && s.id !== isolatedSegId) return null;
                      const pos = s.routedCoords?.length >= 2 ? s.routedCoords : s.points.map((p) => [p.lat, p.lng]);
                      return (
                        <Polyline key={s.id} positions={pos} pathOptions={{ color: s.color, weight: 4, opacity: 0.85 }} />
                      );
                    })}
                    {segments.map((s) =>
                      (isolatedSegId && s.id !== isolatedSegId) ? null :
                        (s.routedCoords?.length >= 4 ? <DirectionArrows key={`${s.id}-arr`} coords={s.routedCoords} /> : null)
                    )}

                    {/* Paragens (kind:"stop", excl. extremos) */}
                    {segments.flatMap((s) => {
                      if (isolatedSegId && s.id !== isolatedSegId) return [];
                      // GO-DOUBLE-MARKER-FIX (2026-07-16) — os extremos de cada
                      // segmento (s.points[0]/[last]) já são desenhados como GOTA
                      // pela camada `buildAnnotatedMarkers` (início/fim/junção).
                      // Nas gravações Go esses extremos SÃO localidades
                      // `kind:"stop"`, por isso apareciam TAMBÉM como QUADRADO
                      // aqui → marcador duplo. Excluímos os quadrados que caem
                      // sobre uma gota (mesma precisão toFixed(4) do agrupador).
                      const _ptK = (p) => (p ? `${Number(p.lat).toFixed(4)}|${Number(p.lng).toFixed(4)}` : null);
                      const _pts = Array.isArray(s.points) ? s.points : [];
                      const _endpointKeys = new Set([_ptK(_pts[0]), _ptK(_pts[_pts.length - 1])].filter(Boolean));
                      return stopsOf(s)
                          .filter((poi) => poi.role !== "start" && poi.role !== "end"
                            && !hiddenPoiCats.has(poi.category || "other")
                            && !_endpointKeys.has(_ptK(poi)))
                          .map((poi, pidx) => {
                            const poiName = localizedText(poi.name) || `${poi.lat.toFixed(4)}, ${poi.lng.toFixed(4)}`;
                            const poiPhotos = Array.isArray(poi.photos) ? poi.photos.filter((u) => typeof u === "string" && u) : [];
                            const hasPhotos = poiPhotos.length > 0;
                            return (
                            <Marker key={`poi-${s.id}-${poi.id || pidx}`} position={[poi.lat, poi.lng]} icon={poiIcon(poi.category || (poi.poi_kind === "fuel" ? "fuel" : "other"), { hasPhotos })} eventHandlers={hasPhotos ? undefined : { click: () => flyTo(poi.lat, poi.lng) }}>
                              <MapTooltip direction="top" offset={[0, -42]} opacity={0.95}>
                                <div className="text-[11px] font-medium">
                                  <div>{poiName}</div>
                                  <div className="text-muted-foreground">{t(`editor.poi.categories.${poi.category}`, poi.category || "")}</div>
                                  {hasPhotos && (
                                    <div className="flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-orange-500">
                                      <Camera className="h-3 w-3" aria-hidden />
                                      {t("public_route.click_to_see_photos", "Clique para ver fotos")}
                                    </div>
                                  )}
                                </div>
                              </MapTooltip>
                              {hasPhotos && (
                                <Popup minWidth={180} maxWidth={260} autoPan={false}>
                                  <div className="space-y-1.5" data-testid={`public-route-poi-popup-${poi.id || pidx}`}>
                                    <div className="text-[12px] font-semibold leading-snug">{poiName}</div>
                                    <PointPhotos photos={poiPhotos} thumbSize={52} testIdPrefix={`public-route-poi-${poi.id || pidx}`} />
                                  </div>
                                </Popup>
                              )}
                            </Marker>
                            );
                          });
                    })}

                    {/* Via-shaping dots (pontos de estrada) */}
                    {segments.map((s) =>
                      (isolatedSegId && s.id !== isolatedSegId) ? null :
                        <ViaShapingDots key={`via-${s.id}`} waypoints={roadsOf(s)} color={s.color} />
                    )}

                    {/* Annotated markers: joins / waypoints (start/end são
                        agora desenhados a partir de `boundaries` — extremos
                        reais da geometria, ver routeRenderModel). */}
                    {buildAnnotatedMarkers(isolatedSegId ? segments.filter((s) => s.id === isolatedSegId) : segments)
                      .filter((g) => {
                        // Ignora o grupo do início/fim global (role start/end):
                        // esses passam a ser renderizados por `boundaries`.
                        const isStartEnd = g.items.some((it) => it.role === "start" || it.role === "end");
                        if (isStartEnd) return false;
                        const hasJoin = g.items.some((it) => it.role === "join");
                        const hasWp = g.items.some((it) => it.role === "wp");
                        if (hasJoin) return showJoins;
                        if (hasWp) return showWaypoints;
                        return true;
                      })
                      .map((g, gi) => {
                        const joinCount = g.items.filter((it) => it.role === "join").length;
                        const isJunction = joinCount >= 2;
                        let icon;
                        if (isJunction) icon = junctionIcon(g.items.map((it) => it.color));
                        else if (g.items.length > 0 && g.items.every((it) => it.role === "wp" && it.poi_kind === "fuel")) icon = fuelIcon(g.items[0].color);
                        else icon = segmentIcon(g.items[0].color);
                        const offsetY = isJunction ? -30 : -22;
                        const seen = new Set();
                        const labels = g.items.map((it) => it.label).filter((l) => { if (seen.has(l)) return false; seen.add(l); return true; });
                        return (
                          <Marker key={`marker-${gi}`} position={[g.lat, g.lng]} icon={icon}>
                            <MapTooltip direction="top" offset={[0, offsetY]} opacity={0.95} className="map-tooltip-compact">
                              {labels.join(" · ")}
                            </MapTooltip>
                          </Marker>
                        );
                      })}

                    {/* Início / Fim / Circular — extremos REAIS da geometria
                        (fonte única routeRenderModel), idêntico ao mapa do
                        passeio. */}
                    {showStartEnd && boundaries.start && boundaries.isCircular && (
                      <Marker position={[boundaries.start.lat, boundaries.start.lng]} icon={circularIcon}>
                        <MapTooltip direction="top" offset={[0, -38]} opacity={0.95} className="map-tooltip-compact">
                          {boundaries.start.name || t("public_route.circular", "Circular")}
                        </MapTooltip>
                      </Marker>
                    )}
                    {showStartEnd && boundaries.start && !boundaries.isCircular && (
                      <Marker position={[boundaries.start.lat, boundaries.start.lng]} icon={startIcon}>
                        <MapTooltip direction="top" offset={[0, -30]} opacity={0.95} className="map-tooltip-compact">
                          {boundaries.start.name || t("public_route.start", "Início")}
                        </MapTooltip>
                      </Marker>
                    )}
                    {showStartEnd && boundaries.end && !boundaries.isCircular && (
                      <Marker position={[boundaries.end.lat, boundaries.end.lng]} icon={finishIcon}>
                        <MapTooltip direction="top" offset={[0, -30]} opacity={0.95} className="map-tooltip-compact">
                          {boundaries.end.name || t("public_route.end", "Fim")}
                        </MapTooltip>
                      </Marker>
                    )}

                    {segments.length > 0 && (
                      <FitAllSegments segments={segments} resizeTrigger={`${fullscreen}|${activeAltPanel}|${recenterTrigger}`} isolatedSegId={isolatedSegId} />
                    )}
                    {hoveredCoord && <Marker position={[hoveredCoord.lat, hoveredCoord.lng]} icon={hoverIcon} interactive={false} />}
                    {hoveredWaypoint && (
                      <HoverHighlight
                        point={hoveredWaypoint}
                        tooltipOffset={hoveredWaypoint.kind === "poi" ? [0, -42] : [0, -18]}
                        label={
                          hoveredWaypoint.kind === "poi" ? (
                            <div className="text-[11px] font-medium">
                              <div>{hoveredWaypoint.name}</div>
                              <div className="text-muted-foreground">
                                {t(`editor.poi.categories.${hoveredWaypoint.category || "other"}`, { defaultValue: t("editor.poi.categories.other") })}
                              </div>
                            </div>
                          ) : (hoveredWaypoint.name || null)
                        }
                        showTooltip={!tooltipsVisible}
                      />
                    )}
                  </MapContainer>
                </MapArrowsContext.Provider>
              </MapTooltipsContext.Provider>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">{t("public_route.no_geometry", "Sem geometria para mostrar.")}</div>
            )}
          </div>

          {/* Elevation panels (below the map, like the editor) */}
          {showProfile && (
            <div className="shrink-0">
              <ElevationProfileWeb coordinates={elevCoords} position="middle" onHoverPoint={setHoveredCoord} />
            </div>
          )}
          {showHistogram && (
            <div className="shrink-0">
              <ElevationHistogramWeb coordinates={elevCoords} position="middle" activeAltitude={hoveredCoord?.alt ?? null} />
            </div>
          )}
        </div>
      </div>

        {/* Slim brand footer bar (mirrors the Editor maximized footer) */}
        <div
          className="shrink-0 flex items-center justify-center px-3 py-1.5 border-t border-border bg-card/95 backdrop-blur-sm text-[11px] text-muted-foreground"
          data-testid="public-route-footer"
        >
          <span>
            ©MotoTrips ·{" "}
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              className="hover:text-primary transition-colors"
              data-testid="public-route-footer-link"
            >
              www.mototrips.net
            </a>
          </span>
        </div>

      </div>

    </div>
  );

  return (
    <div className="fixed inset-0 z-[50] flex flex-col bg-background" data-testid="public-route-page-root">
      <RouteSeo route={route} />
      {shell}
      <Dialog open={metricsOpen} onOpenChange={setMetricsOpen}>
        <DialogContent className="sm:max-w-sm z-[10002]" data-testid="public-route-metrics-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              {t("public_route.metrics_title", "Métricas da rota")}
            </DialogTitle>
            <DialogDescription>
              {t("public_route.metrics_desc", "Uso público desta rota.")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[
              { key: "views", icon: Eye, label: t("public_route.metric_views", "Visualizações"), value: route?.metrics?.views },
              { key: "copies", icon: CopyIcon, label: t("public_route.metric_copies", "Gravações como rota"), value: route?.metrics?.copies },
              { key: "gpx", icon: Download, label: t("public_route.metric_gpx", "Exportações GPX"), value: route?.metrics?.export_gpx },
              { key: "gmaps", icon: MapIcon, label: t("public_route.metric_gmaps", "Exportações Google Maps"), value: route?.metrics?.export_gmaps },
              { key: "likes", icon: Heart, label: t("public_route.metric_likes", "Gostos"), value: route?.metrics?.likes },
            ].map(({ key, icon: Icon, label, value }) => (
              <div
                key={key}
                className="flex flex-col gap-1 rounded-lg border border-border bg-card/60 p-3"
                data-testid={`public-route-metric-${key}`}
              >
                <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Icon className="h-3.5 w-3.5" />{label}
                </span>
                <span className="text-2xl font-bold text-foreground">{value || 0}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <GmapsExportDialog
        open={gmapsOpen}
        onOpenChange={setGmapsOpen}
        segments={segments}
        filename={route?.name}
      />
      <ExportPreflightDialog
        open={gpxDialogOpen}
        onOpenChange={setGpxDialogOpen}
        segments={segments}
        gaps={[]}
        totals={{ km: totals.distanceKm, durationMin: totals.durationMin }}
        onExportGPX={doExportGpx}
        onExportPerStageGPX={doExportPerStageGpx}
        mode="gpx"
      />
    </div>
  );
}
